
import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface Card3DProps {
  children: React.ReactNode;
  className?: string;
  intensity?: 'subtle' | 'medium' | 'strong';
  glowEffect?: boolean;
  interactive?: boolean;
  onClick?: () => void;
}

export const Card3D = ({
  children,
  className,
  intensity = 'medium',
  glowEffect = true,
  interactive = true,
  onClick
}: Card3DProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const intensityMap = {
    subtle: 0.05,
    medium: 0.1,
    strong: 0.2
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current || !interactive) return;

    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const rotateXValue = (e.clientY - centerY) * intensityMap[intensity];
    const rotateYValue = (centerX - e.clientX) * intensityMap[intensity];
    
    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
    setIsHovered(false);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  return (
    <motion.div
      ref={ref}
      className={cn(
        'relative rounded-3xl bg-white/80 backdrop-blur-xl border border-white/20 overflow-hidden',
        'shadow-2xl transition-all duration-300 transform-gpu',
        glowEffect && 'hover:shadow-brand-primary/20',
        onClick && 'cursor-pointer',
        className
      )}
      style={{
        transformStyle: 'preserve-3d',
        perspective: '1000px'
      }}
      animate={{
        rotateX: rotateX,
        rotateY: rotateY,
        scale: isHovered ? 1.02 : 1,
        y: isHovered ? -8 : 0
      }}
      transition={{
        type: 'spring',
        damping: 20,
        stiffness: 300
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      onClick={onClick}
    >
      {/* Gradient overlay */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-brand-primary/5 via-transparent to-brand-secondary/5 opacity-0"
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Glass effect */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0"
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Spotlight effect */}
      <motion.div
        className="absolute inset-0 opacity-0"
        style={{
          background: `radial-gradient(600px circle at ${rotateY * 5 + 50}% ${-rotateX * 5 + 50}%, rgba(255,255,255,0.1), transparent 40%)`
        }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />
      
      <div className="relative z-10">
        {children}
      </div>
    </motion.div>
  );
};
